import { FC } from 'react'
import { graphql } from 'relay-runtime'
import { PostList_posts$key } from './__generated__/PostList_posts.graphql'
import { PostList_post$key } from './__generated__/PostList_post.graphql'
import { useFragment } from 'react-relay/hooks'
import Link from 'next/link'
import { router } from '@lifedot/router'
import { PostCard } from '@lifedot/components/PostCard'
import { fill } from '@lifedot/utils'

import { Pagination, PaginationItem } from '@lifedot/atoms/Pagination'
import { mq } from '@lifedot/styles/mediaQuery'
import { css } from '@emotion/react'
import { typography } from '@lifedot/styles/typography'

const styles = {
  title: css({
    marginBottom: 8,
    [mq('sp')]: {
      padding: '8px 8px 0'
    }
  }),
  list: css({
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: -8,
    [mq('sp')]: {
      padding: '0 8px'
    }
  }),
  item: css({
    display: 'flex',
    width: 'calc(50% - 8px)',
    marginBottom: 8,
    [mq('pc')]: {
      ':nth-of-type(odd)': {
        marginRight: 8
      }
    },
    [mq('sp')]: {
      width: '100%'
    }
  }),
  anchor: css({
    textDecoration: 'none',
    display: 'flex'
  }),
  nav: css({
    marginTop: 28,
    ul: {
      justifyContent: 'center'
    }
  })
}

interface PostListProps {
  posts: PostList_posts$key
  post: PostList_post$key | null
  hideTitle?: boolean
}

export const PostList: FC<PostListProps> = ({
  posts,
  post,
  hideTitle = false
}) => {
  const { items, pagination } = useFragment(fragment, posts)
  const postData = useFragment(postFragment, post)
  if (!postData || items.length < 1) return null

  return (
    <div>
      {!hideTitle && (
        <h1 css={[typography.headingXL, styles.title]}>
          {pagination.currentPageNumber > 1
            ? `(${pagination.currentPageNumber}ページ目)`
            : ''}
          {postData.h1}
        </h1>
      )}
      <ul css={styles.list}>
        {items.map(({ h1, image, slug, modified_at }) => (
          <li css={styles.item} key={slug}>
            <Link href={router.post(slug)} passHref prefetch={false}>
              <a css={styles.anchor}>
                <PostCard
                  title={h1}
                  thumbnailUrl={image?.url}
                  thumbnailAlt={image?.alt}
                  lastUpdate={modified_at}
                />
              </a>
            </Link>
          </li>
        ))}
      </ul>
      <div css={styles.nav}>
        <Pagination current={pagination.currentPageNumber}>
          {fill(1, pagination.lastPageNumber).map((num) => (
            <PaginationItem
              index={num}
              key={num}
              href={router.post(postData.slug, num)}
            />
          ))}
        </Pagination>
      </div>
    </div>
  )
}

const postFragment = graphql`
  fragment PostList_post on Post {
    slug
    h1
  }
`

const fragment = graphql`
  fragment PostList_posts on PostResult {
    pagination {
      currentPageNumber
      lastPageNumber
    }
    items {
      slug
      h1
      image {
        alt
        url
      }
      modified_at
    }
  }
`
